import {
  DragEndEvent,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useState } from "react";
import {
  getGetDoclistQueryKey,
  useCreateSectionFromPage,
  useReorderChatDoclistPages,
} from "~/api/chat/chat";
import { getListCustomReportsByCaseQueryKey } from "~/api/custom-reports/custom-reports";
import { DoclistTreeItem, DoclistTreeItemSectionsItem } from "~/api/model";
import { removeEmptySectionAndCategory } from "~/helpers";
import { trackEvent } from "~/utils/analytics";
import logger from "~/utils/logger";

export function useDndForPages(
  items: DoclistTreeItem[],
  setItems: React.Dispatch<React.SetStateAction<DoclistTreeItem[]>>,
  organizationId: string,
  projectId: string,
  chatId: string,
  isViewOnly: boolean,
  updatedAt: number | undefined,
  onUpdatedAtChange: (updatedAt: number) => void,
  onDoclistOutdated: () => void
) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [selectedPageId, setSelectedPageId] = useState<string | null>(null);
  const [createdSectionId, setCreatedSectionId] = useState<string | null>(null);
  const [openSections, setOpenSections] = useState<string[]>([]);
  const { mutateAsync: reorderPages } = useReorderChatDoclistPages();
  const { mutateAsync: createSection } = useCreateSectionFromPage();
  const queryClient = useQueryClient();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragPageStart = (event: DragStartEvent) => {
    if (isViewOnly || !updatedAt) return;
    if (event.active.data.current?.type !== "page") return;
    const { active } = event;
    setActiveId(active.id as string);

    trackEvent({
      eventName: "app/doc_list/move_page/drag_start",
    });
  };

  const handleDragPageEnd = async (event: DragEndEvent) => {
    if (isViewOnly || !updatedAt) return;

    const { active, over } = event;
    setActiveId(null);

    if (!over || active.data.current?.type !== "page") return;

    const newItems = items.map((item) => ({
      ...item,
      sections: item.sections.map((section) => ({
        ...section,
        pages: section.pages ? [...section.pages] : [],
        pageIds: section.pageIds ? [...section.pageIds] : [],
      })),
    }));

    const flatSections = newItems.flatMap((item) => item.sections);

    const activePageData = active.data.current;
    const activePageIndex = active.data.current.sortable.index;

    const overData = over.data.current;

    const overId = over.id as string;

    // Drop page in empty category (WIP)
    if (overId.startsWith("empty-category-")) {
      return;
    }

    // Determine source section
    const sourceSectionId = activePageData.sectionId;
    const sourceSection = flatSections.find(
      (section) => section.id === sourceSectionId
    );
    if (!sourceSection) return;

    const isNewSectionTarget = over.id.toString().includes("new-section");

    // Determine target section
    const targetSectionId = isNewSectionTarget
      ? over.id.toString().replace("new-section-", "")
      : overData?.type === "page"
        ? overData.sectionId
        : over.id.toString().replace("section-", "");
    const targetSection = flatSections.find(
      (section) => section.id === targetSectionId
    );

    if (!targetSection) return;

    if (isNewSectionTarget) {
      const targetSectionIndex = flatSections.findIndex(
        (section) => section.id === targetSectionId
      );
      const prevId =
        targetSectionIndex > 0 ? flatSections[targetSectionIndex - 1].id : null;
      const targetCategory = newItems.find(
        (item) => item.category.id === targetSection.categoryId
      );
      if (!targetCategory) return;

      const movedPage = sourceSection.pages?.find(
        (page) => page.id === activePageData.pageId
      );
      if (!movedPage) return;

      // Create new section
      const newSectionId = "00000000-0000-0000-0000-000000000000";
      setCreatedSectionId(newSectionId);
      const newSection: DoclistTreeItemSectionsItem = {
        id: newSectionId,
        name: "Untitled",
        pageCount: 1,
        categoryId: targetCategory.category.id,
        pageIds: [movedPage?.id],
        pages: movedPage ? [movedPage] : [],
      };
      const movedPageIndex = sourceSection?.pages?.findIndex(
        (page) => page.id === movedPage.id
      );
      if (movedPageIndex === undefined || movedPageIndex === -1) return;
      sourceSection.pages?.splice(movedPageIndex, 1);
      sourceSection.pageIds?.splice(movedPageIndex, 1);
      sourceSection.pageCount = sourceSection.pageCount - 1;

      // Check if there are pages left in the section
      removeEmptySectionAndCategory(newItems, sourceSection);

      const categoryTargetSectionIndex = targetCategory.sections.findIndex(
        (section) => section.id === targetSectionId
      );
      targetCategory.sections.splice(categoryTargetSectionIndex, 0, {
        ...newSection,
        pages: newSection.pages ? [...newSection.pages] : [],
        pageIds: newSection.pageIds ? [...newSection.pageIds] : [],
      });
      setItems([...newItems]);
      try {
        const response = await createSection({
          organizationId,
          projectId,
          chatId,
          data: {
            pageId: movedPage.id,
            title: newSection.name,
            folderCategoryId: targetCategory.category.id,
            target: {
              prevId: prevId,
            },
            updatedAt: updatedAt,
          },
        });
        onUpdatedAtChange(response.updatedAt);
        const createdSection = response.section;
        // Change temporary section to real
        const categoryIndex = newItems.findIndex(
          (item) => item.category.id === targetCategory.category.id
        );
        if (categoryIndex !== -1) {
          const sectionIndex = newItems[categoryIndex].sections.findIndex(
            (section) => section.id === newSection.id
          );
          if (sectionIndex !== -1) {
            newItems[categoryIndex].sections[sectionIndex] = {
              ...createdSection,
              pages: createdSection.pages ? [...createdSection.pages] : [],
              pageIds: createdSection.pageIds
                ? [...createdSection.pageIds]
                : [],
            };
            setItems([...newItems]);
            setCreatedSectionId(createdSection.id);
            setOpenSections((prev) => [...prev, createdSection.id]);
          }
        }
        queryClient.invalidateQueries({
          queryKey: getGetDoclistQueryKey(organizationId, projectId),
        });
        queryClient.invalidateQueries({
          queryKey: getListCustomReportsByCaseQueryKey(
            organizationId,
            projectId
          ),
        });
      } catch (error) {
        setItems([...items]);
        if (error instanceof AxiosError) {
          if (error.response?.status === 409) {
            onDoclistOutdated();
          }
        }
        logger.error("Failed to create section:", error);
      }
      return;
    }

    const movedPage = sourceSection.pages?.[activePageIndex];
    if (!movedPage) return;

    const movedPageIndex = activePageIndex;

    let newPageIndex = null;
    if (overData?.type === "page") {
      newPageIndex = over.data?.current?.sortable.index;
    } else {
      newPageIndex = targetSection.pages?.length;
    }

    if (newPageIndex === undefined || newPageIndex === -1) return;
    if (newPageIndex === activePageIndex) {
      if (sourceSectionId === targetSectionId) return;
    }

    sourceSection.pages?.splice(movedPageIndex, 1);
    sourceSection.pageIds?.splice(movedPageIndex, 1);
    sourceSection.pageCount = sourceSection.pageCount - 1;

    targetSection.pages?.splice(newPageIndex, 0, movedPage);
    targetSection.pageIds?.splice(newPageIndex, 0, movedPage.id);
    targetSection.pageCount = targetSection.pageCount + 1;

    // Check if there are pages left in the section
    removeEmptySectionAndCategory(newItems, sourceSection);

    const prevPageId =
      newPageIndex > 0 ? targetSection.pages?.[newPageIndex - 1]?.id : null;
    if (prevPageId === undefined || prevPageId === movedPage.id) return;

    setItems([...newItems]);
    try {
      const response = await reorderPages({
        organizationId,
        projectId,
        chatId,
        data: {
          source: {
            sectionId: sourceSection.id,
            pageId: movedPage.id,
          },
          target: {
            prevId: prevPageId,
            sectionId: targetSection.id,
          },
          updatedAt: updatedAt,
        },
      });
      onUpdatedAtChange(response.updatedAt);
      queryClient.invalidateQueries({
        queryKey: getGetDoclistQueryKey(organizationId, projectId),
      });
      queryClient.invalidateQueries({
        queryKey: getListCustomReportsByCaseQueryKey(organizationId, projectId),
      });
    } catch (error) {
      setItems([...items]);
      if (error instanceof AxiosError) {
        if (error.response?.status === 409) {
          onDoclistOutdated();
        }
      }
      logger.error("Failed to reorder categories:", error);
    }
    return;
  };

  return {
    activeId,
    setActiveId,
    selectedPageId,
    setSelectedPageId,
    sensors,
    handleDragPageStart,
    handleDragPageEnd,
    createdSectionId,
    openSections,
    setOpenSections,
  };
}
